import Emitter from "tiny-emitter"
import { Howl } from "howler"

import { isEmpty, isPresent, isBuilderContext } from "@/lib/utils"

const COUNT_IN_DURATION = 4
const TICKS_PER_SECOND = 4
const DEFAULT_SPRITE_MAP = require("@/assets/workout-beeps.json")

const TIMER_STATUS = {
  INITIALIZED: "initialized",
  COUNTING_IN: "counting_in",
  RUNNING: "running",
  PAUSED: "paused",
  FINISHED: "finished"
}
const TIMER_EVENTS = {
  FINISHED: "finished",
  STARTED: "started",
  PAUSED: "paused"
}
const COUNT_DIRECTIONS = {
  UP: "up",
  DOWN: "down"
}
const DEFAULT_TIMEOUTS = {
  initialized: false,
  finish: null,
  groupStart: null,
  groupEnd: null
}
const FORMATS = {
  CONDENSED: "condensed",
  READABLE: "readable",
  READABLE_SHORT: "readable_short"
}

// NOTE: This needs to be outside of the contructor so that Howler is not re-initialized
let WORKOUT_AUDIO = null
let SOUND_ID = null

const pad = string => string.padStart(2, "0")
const minutesToSeconds = minutes => minutes * 60
const hoursToSeconds = hours => minutesToSeconds(hours * 60)

const toHumanTime = (seconds, format) => {
  const secondPart = (seconds % 60).toString()
  const minutePart = (Math.floor(seconds / 60) % 60).toString()
  const hourPart = (Math.floor(seconds / 3600) % 24).toString()

  let output = []

  output.push(pad(secondPart))
  output.push(pad(minutePart))
  if (hourPart !== "0") output.push(pad(hourPart))

  if (format === FORMATS.READABLE) {
    return output.length === 3
      ? `${hourPart}hr ${minutePart}min ${secondPart}sec`
      : output.length === 2 && parseInt(minutePart) !== 0
      ? `${minutePart}min ${secondPart}sec`
      : `${secondPart}sec`
  } else if (format === FORMATS.READABLE_SHORT) {
    return hourPart !== "0"
      ? `${hourPart}hr ${minutePart !== "0" ? `${minutePart}min` : ""}`
      : minutePart !== "0"
      ? `${minutePart}min`
      : `${secondPart}sec`
  } else {
    return output.reverse().join(":")
  }
}

const calculateHumanTime = (
  elapsedSeconds,
  totalSeconds,
  { countDirection, countIn, format }
) => {
  if (countIn && elapsedSeconds < COUNT_IN_DURATION) {
    if (elapsedSeconds < 3) {
      return String(3 - elapsedSeconds)
    }

    return "Go!"
  }

  let seconds = Math.round(
    countDirection === COUNT_DIRECTIONS.DOWN
      ? totalSeconds - elapsedSeconds
      : countIn
      ? elapsedSeconds - COUNT_IN_DURATION
      : elapsedSeconds
  )

  seconds = seconds > 0 ? seconds : 0

  return toHumanTime(seconds, format)
}

const initializeAudio = options => {
  if (isBuilderContext()) return options.callback()

  const { spriteMap, callback, isMuted } = options || {}
  const sprite = isPresent(spriteMap) ? spriteMap : DEFAULT_SPRITE_MAP

  // Default sprite map wasn't triggering onload, so call manually
  if (isEmpty(spriteMap) && isPresent(callback)) {
    setTimeout(callback, 400)
  }

  if (!sprite.sprite["count-in"]) {
    throw 'Sprite map must define "count-in"'
  }

  SOUND_ID = null

  if (isPresent(WORKOUT_AUDIO)) {
    WORKOUT_AUDIO.stop()
    WORKOUT_AUDIO.unload()
  }

  WORKOUT_AUDIO = new Howl({
    src: sprite.urls,
    sprite: sprite.sprite,
    format: sprite.format,
    onload: isPresent(spriteMap) && isPresent(callback) ? callback : null,
    preload: true,
    mute: isMuted,
    html5: true // https://github.com/goldfire/howler.js/issues/1326#issuecomment-687946032
  })
}

const clearAudio = () => {
  if (isPresent(WORKOUT_AUDIO)) {
    WORKOUT_AUDIO.unload()
  }
  WORKOUT_AUDIO = null
  SOUND_ID = null
}

class Timer {
  constructor(
    options = {
      startAt: 0,
      seconds: null,
      countDirection: COUNT_DIRECTIONS.UP,
      countIn: false,
      format: FORMATS.CONDENSED
    }
  ) {
    this.startedAt = 0
    this.secondsLeft = 0
    this.elapsedSeconds = options.startAt || 0
    this.percentComplete = 0
    this.isFixedDuration = !!options.seconds
    this.emitter = new Emitter()
    this.hasAudio = !!(
      isPresent(WORKOUT_AUDIO) &&
      WORKOUT_AUDIO.pause &&
      WORKOUT_AUDIO.play
    )

    if (this.isFixedDuration) {
      this.totalSeconds = options.countIn
        ? options.seconds + COUNT_IN_DURATION
        : options.seconds
    }

    this.status = TIMER_STATUS.INITIALIZED
    this.interval = null
    this.timeouts = DEFAULT_TIMEOUTS
    this.options = options
    this.setHumanTime()
  }

  start(audio = null) {
    const wasPaused = this.status === TIMER_STATUS.PAUSED

    if (this.isFixedDuration) {
      this.secondsLeft = this.totalSeconds - this.elapsedSeconds

      if (!this.timeouts.initialized) {
        if (isPresent(audio)) {
          this.initializeTimeouts(audio)
        } else {
          this.initializeTimeouts()
        }
      }

      if (this.secondsLeft <= 0) this.status = TIMER_STATUS.FINISHED
      if (this.status === TIMER_STATUS.FINISHED) return
    }

    if (!wasPaused && this.options.countIn && this.hasAudio) {
      this.playAudio("count-in")
      setTimeout(() => (SOUND_ID = null), COUNT_IN_DURATION * 1000 - 5)
    }

    this.startedAt = +new Date() - this.elapsedSeconds * 1000
    this.interval = setInterval(() => this.tick(), 1000 / TICKS_PER_SECOND)

    if (wasPaused) {
      this.emitter.emit(TIMER_EVENTS.STARTED)
    }

    this.tick()
  }

  pause() {
    this.status = TIMER_STATUS.PAUSED
    if (this.hasAudio) WORKOUT_AUDIO.pause()

    this.clearInterval()
    this.clearTimeout()

    this.emitter.emit(TIMER_EVENTS.PAUSED)
  }

  finish() {
    this.status = TIMER_STATUS.FINISHED
    this.emitter.emit(TIMER_EVENTS.FINISHED)
    SOUND_ID = null
    this.clearInterval()
    this.clearTimeout()
  }

  mute(muted = true) {
    if (isPresent(WORKOUT_AUDIO)) {
      WORKOUT_AUDIO.mute(muted)
    }
  }

  tick() {
    const secondsSinceStart = (+new Date() - this.startedAt) / 1000
    this.elapsedSeconds = Math.floor(secondsSinceStart)

    if (this.isFixedDuration) {
      this.secondsLeft = this.totalSeconds - this.elapsedSeconds

      const skipSeconds = this.options.countIn ? COUNT_IN_DURATION : 0
      this.percentComplete = Math.max(
        0,
        ((secondsSinceStart - skipSeconds + 1 / TICKS_PER_SECOND) /
          (this.totalSeconds - skipSeconds)) *
          100
      )
    }

    if (this.options.countIn) {
      if (this.status !== TIMER_STATUS.RUNNING) {
        if (this.elapsedSeconds === COUNT_IN_DURATION) {
          this.emitter.emit(TIMER_EVENTS.STARTED)
        }

        if (this.elapsedSeconds < COUNT_IN_DURATION) {
          this.status = TIMER_STATUS.COUNTING_IN
        }

        if (this.elapsedSeconds >= COUNT_IN_DURATION) {
          this.status = TIMER_STATUS.RUNNING
        }
      }
    } else {
      this.status = TIMER_STATUS.RUNNING
    }

    this.setHumanTime()
  }

  reset() {
    this.clearInterval()
    this.clearTimeout()
    this.startedAt = 0
    this.elapsedSeconds = 0
    this.percentComplete = 0
    this.status = TIMER_STATUS.INITIALIZED
    if (WORKOUT_AUDIO) WORKOUT_AUDIO.stop()

    this.setHumanTime()
  }

  initializeTimeouts(
    audio = { start: {}, end: { sprite: "count-in", duration: 3 } }
  ) {
    const withFinalZero =
      this.options.countDirection === COUNT_DIRECTIONS.DOWN &&
      this.options.countIn
        ? 1 // mainly for final beep
        : 0

    const finish = setTimeout(
      () => this.finish(),
      (this.secondsLeft + withFinalZero) * 1000
    )

    const groupEnd = setTimeout(() => {
      if (isPresent(audio.end)) {
        this.playAudio(audio.end.sprite)
      }
    }, (this.secondsLeft - audio.end.duration) * 1000)

    const groupStart = setTimeout(() => {
      if (isPresent(audio.start)) {
        this.playAudio(audio.start.sprite)
      }
    }, (this.options.countIn ? COUNT_IN_DURATION : 0) * 1000)

    this.timeouts = {
      initialized: true,
      finish,
      groupStart,
      groupEnd
    }
  }

  clearInterval() {
    clearInterval(this.interval)
    this.interval = null
  }

  clearTimeout() {
    Object.keys(this.timeouts).map(key => {
      if (key !== "initialized") {
        isPresent(this.timeouts[key]) && clearTimeout(this.timeouts[key])
      }
    })

    this.timeouts = DEFAULT_TIMEOUTS
  }

  setHumanTime() {
    if (
      this.status === TIMER_STATUS.INITIALIZED &&
      this.options.seconds &&
      this.options.countDirection === COUNT_DIRECTIONS.DOWN
    ) {
      this.humanTime = toHumanTime(this.options.seconds, this.options.format)
    } else {
      this.humanTime = calculateHumanTime(
        this.elapsedSeconds,
        this.totalSeconds,
        this.options
      )
    }
  }

  updateSeconds(newSeconds) {
    const newTotalSeconds = this.options.countIn
      ? newSeconds + COUNT_IN_DURATION
      : newSeconds

    // is tested only for workouts usage in cypress/workouts-new.js
    if (newTotalSeconds - this.elapsedSeconds <= 3) {
      return
    }

    this.options.seconds = newSeconds
    this.totalSeconds = newTotalSeconds

    if (this.status === TIMER_STATUS.INITIALIZED) {
      this.setHumanTime()
    } else {
      this.secondsLeft = Math.max(this.totalSeconds - this.elapsedSeconds, 0)

      this.clearInterval()
      this.clearTimeout()

      if (this.status === TIMER_STATUS.RUNNING) {
        this.initializeTimeouts()

        this.startedAt = +new Date() - this.elapsedSeconds * 1000
        this.interval = setInterval(() => this.tick(), 1000 / TICKS_PER_SECOND)

        this.tick()
      } else {
        this.setHumanTime()
      }
    }
  }

  getSeconds() {
    return this.options.seconds
  }

  playAudio(sprite) {
    if (this.hasAudio && !WORKOUT_AUDIO.playing()) {
      if (isPresent(SOUND_ID)) {
        WORKOUT_AUDIO.play(sprite)
      } else {
        SOUND_ID = WORKOUT_AUDIO.play(sprite)
      }
    }
  }

  on(eventName, callback) {
    return this.emitter.on(eventName, callback)
  }
}

export default Timer
export {
  TIMER_STATUS,
  TIMER_EVENTS,
  COUNT_DIRECTIONS,
  FORMATS,
  minutesToSeconds,
  hoursToSeconds,
  initializeAudio,
  clearAudio
}
